import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon } from 'lucide-react';
import { ContinueBar } from './ContinueBar';
import { DataTag } from './DataTag';
import { DistributionBars } from './DistributionBars';
import { findings, nextPulse } from '../../data/results';
import { chapters } from '../../data/chapters';
interface NextPulseScreenProps {
  interpretations: Record<string, string>;
  selected: string | null;
  onSelect: (id: string | null) => void;
  onContinue: () => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function NextPulseScreen({
  interpretations,
  selected,
  onSelect,
  onContinue
}: NextPulseScreenProps) {
  const answered = findings.filter(f => interpretations[f.id]);
  return <div className="relative h-full bg-mist-50" data-id="element-621" data-mp="2o5mo6yyech94">
      <div className="h-full overflow-y-auto px-6 pb-36 pt-24 no-scrollbar md:pt-28" data-id="element-622" data-mp="3bx1uf2tanipn">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-pulse-iris" data-id="element-623" data-mp="3gni88o1116cj">
          <SparklesIcon className="h-3.5 w-3.5" data-id="element-624" data-mp="2gs5n9p426b71" />
          {nextPulse.eyebrow}
        </p>
        <motion.h2 initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        ease
      }} className="mt-3 font-serif text-[32px] leading-[1.05] text-navy-900" data-id="element-625" data-mp="2btucnc5xvwnr">
          {nextPulse.headline}
        </motion.h2>

        {answered.length > 0 && <section className="mt-6 rounded-3xl border border-navy-900/[0.08] bg-white p-5" aria-label="Your interpretations" data-id="element-626" data-mp="2zi56vwh4qy32">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400" data-id="element-627" data-mp="2ujtw9jj0gjjs">Your interpretations</p>
            <ol className="mt-3 space-y-3" data-id="element-628" data-mp="39f04md5kwuga">
              {answered.map(f => {
            const chapter = chapters.find(c => c.id === f.chapterId);
            const opt = f.interpretation.options.find(o => o.id === interpretations[f.id]);
            return <li key={f.id} className="flex gap-3" data-id="element-629" data-mp="1a634e1iwk7qf">
                    <span className="text-[11px] font-semibold tabular-nums" style={{
                color: chapter?.accent
              }} data-id="element-630" data-mp="1zt5t0r3pxloa">
                      {f.number}
                    </span>
                    <span className="text-[14px] leading-snug text-navy-900" data-id="element-631" data-mp="28tc09v8tppf0">{opt?.label}</span>
                  </li>;
          })}
            </ol>
          </section>}

        <section className="mt-8" aria-label="First question of the next Pulse" data-id="element-632" data-mp="2ir8cyrhessf8">
          <h3 className="font-serif text-[22px] leading-tight text-navy-900" data-id="element-633" data-mp="2ipfdbw57r8gq">{nextPulse.question}</h3>
          {!selected ? <ul className="mt-5 space-y-2.5" data-id="element-634" data-mp="2rj1uag4ri75v">
              {nextPulse.options.map((o, i) => <motion.li key={o.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.2 + i * 0.06,
            ease
          }} data-id="element-635" data-mp="2wj6ccalbj9y7" data-mpi={i}>
                  <button type="button" onClick={() => onSelect(o.id)} className="flex min-h-[56px] w-full items-center gap-3.5 rounded-2xl border border-navy-900/10 bg-white px-4 py-3 text-left shadow-[0_1px_2px_rgba(15,36,66,0.05)] transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-636" data-mp="36h2mp06fm0v9">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-navy-900/15 text-[11px] font-semibold text-navy-600" data-id="element-637" data-mp="fyb5ofvc1d7h">
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-[14px] font-medium leading-snug text-navy-900" data-id="element-638" data-mp="13tdr0duxgyj7">{o.label}</span>
                  </button>
                </motion.li>)}
            </ul> : <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease
        }} className="mt-5 rounded-3xl border border-navy-900/[0.08] bg-white p-5" aria-live="polite" data-id="element-639" data-mp="1wbi16wioypfr">
              <DataTag kind="interpretation" detail="n = [n]" data-id="element-640" data-mp="1h1us297keccc" />
              <div className="mt-4" data-id="element-641" data-mp="123kd6bk26tm0">
                <DistributionBars options={nextPulse.options} accent="#5468D8" highlightId={selected} delay={0.15} data-id="element-642" data-mp="124lxz7qvmugx" />
              </div>
              <button type="button" onClick={() => onSelect(null)} className="mt-4 text-[12px] text-slate-500 underline-offset-2 hover:text-navy-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-900/30" data-id="element-643" data-mp="174q5w9dpygvj">
                Change my answer
              </button>
            </motion.div>}
        </section>
      </div>
      <ContinueBar label={selected ? 'Stay in the conversation' : 'Skip to stay involved'} onClick={onContinue} accent="#5468D8" data-id="element-644" data-mp="2dr2i9uj4p9ml" />
    </div>;
}